import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SiteFooter from '../components/SiteFooter';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Plus, Trash2, ChevronRight } from 'lucide-react';
import TutorialLaunchButton from '../components/tutorial/TutorialLaunchButton';
import ThemeToggle from '../components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * LearnMode — hub page listing all Learn Mode topics and letting the user
 * create a new one. Individual topic lifecycle lives in LearnTopic.js.
 */
const LearnMode = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', learning_intent: '' });

  useEffect(() => {
    fetchTopics();
    // Mount-only: refresh happens after user actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authHeaders = () => {
    const token = localStorage.getItem('sage_token');
    if (!token) {
      navigate('/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchTopics = async () => {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const res = await axios.get(`${API}/learn/topics`, { headers });
      setTopics(res.data || []);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
      toast.error('Failed to load learning topics');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const title = form.title.trim();
    const intent = form.learning_intent.trim();
    if (!title || !intent) {
      toast.error('Title and learning intent are required');
      return;
    }
    setCreating(true);
    try {
      const headers = authHeaders();
      const res = await axios.post(
        `${API}/learn/topics`,
        { title, learning_intent: intent },
        { headers }
      );
      toast.success('Topic created');
      setForm({ title: '', learning_intent: '' });
      setShowCreate(false);
      navigate(`/learn/${res.data.topic_id}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to create topic');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (topicId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this topic and all its materials & notes?')) return;
    try {
      await axios.delete(`${API}/learn/topics/${topicId}`, { headers: authHeaders() });
      toast.success('Topic deleted');
      fetchTopics();
    } catch (err) {
      toast.error('Failed to delete topic');
    }
  };

  const statusLabel = (s) => ({
    draft: 'Materials needed',
    assessing: 'Assessment in progress',
    planning: 'Plan pending',
    tutoring: 'In session',
    complete: 'Complete',
  }[s] || s);

  return (
    <div className="min-h-screen bg-obsidian">
      <div className="overlay-noise" />
      <div className="relative z-10">
        {/* Header */}
        <div className="border-b border-white/5 bg-stone-950/50 backdrop-blur-sm">
          <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-4 py-2 transition-all"
                data-testid="learn-back-button"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1
                  className="text-2xl font-light tracking-tight text-zinc-100"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  Learn Mode
                </h1>
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mt-1">
                  Personalized tutoring on any topic
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreate(!showCreate)}
              className="frost-button rounded-sm px-5 py-2"
              data-testid="new-topic-button"
            >
              <Plus className="w-4 h-4" />
              New Topic
            </Button>
          </div>
        </div>

        {/* Floating tour re-entry — bottom-right, unobtrusive. Sits outside
            the header row because the header here is space-between with the
            title on the left and the "New Topic" CTA on the right. */}
        <div className="fixed bottom-6 right-6 z-20 flex flex-col gap-2 items-end">
          <TutorialLaunchButton variant="icon" />
          <ThemeToggle size="sm" className="border border-white/14 bg-stone-950/40 backdrop-blur-sm rounded-sm" />
        </div>

        <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
          {/* Create form */}
          {showCreate && (
            <form
              onSubmit={handleCreate}
              className="bg-stone-950/50 border border-white/10 rounded-sm p-6 space-y-4"
              data-testid="new-topic-form"
            >
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2 block">
                  Topic title
                </label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Advanced React Patterns"
                  className="bg-stone-900/60 border border-white/10 text-white"
                  data-testid="topic-title-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2 block">
                  Learning intent
                </label>
                <textarea
                  value={form.learning_intent}
                  onChange={(e) => setForm({ ...form, learning_intent: e.target.value })}
                  placeholder="What do you want to be able to do after this? Be specific — the more precise, the better the plan."
                  className="w-full bg-stone-900/60 border border-white/10 text-white p-3 rounded-sm min-h-24 text-sm placeholder:text-zinc-500"
                  data-testid="topic-intent-input"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={creating}
                  className="frost-button rounded-sm px-5 py-2"
                  data-testid="create-topic-submit"
                >
                  {creating ? 'Creating…' : 'Create topic'}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-5 py-2"
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {/* Topics list */}
          {loading ? (
            <p className="text-zinc-500 text-sm py-12 text-center">Loading topics…</p>
          ) : topics.length === 0 ? (
            <div className="bg-stone-950/50 border border-white/5 rounded-sm p-12 text-center">
              <BookOpen className="w-10 h-10 text-zinc-400 mx-auto mb-4" />
              <p className="text-zinc-300 mb-2">No learning topics yet</p>
              <p className="text-zinc-500 text-sm mb-6">
                Upload material, answer a diagnostic, and let SAGE tutor you.
              </p>
              <Button
                onClick={() => setShowCreate(true)}
                className="threshold-button rounded-sm px-8 py-4 text-lg"
                data-testid="empty-state-create-button"
                aria-label="Create your first learning topic"
              >
                <span>Create your first topic</span>
                <span className="threshold-button-arrow" aria-hidden="true">→</span>
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="topics-list">
              {topics.map((t) => (
                <div
                  key={t.topic_id}
                  onClick={() => navigate(`/learn/${t.topic_id}`)}
                  className="bg-stone-950/50 border border-white/5 rounded-sm p-5 cursor-pointer hover:border-white/15 transition-all group"
                  data-testid={`topic-card-${t.topic_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-light text-white truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                          {t.title}
                        </h3>
                        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 border border-white/10 px-2 py-0.5 rounded-sm shrink-0">
                          {statusLabel(t.status)}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-400 mb-3 line-clamp-2">
                        {t.learning_intent}
                      </p>
                      {/* Progress bar */}
                      {t.stage_count > 0 && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-[11px] text-zinc-400 mb-1">
                            <span>
                              Stage {(t.current_stage_index || 0) + 1} of {t.stage_count}
                            </span>
                            <span className="font-mono">{t.completion_percentage || 0}%</span>
                          </div>
                          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-white/60 transition-all"
                              style={{ width: `${t.completion_percentage || 0}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={(e) => handleDelete(t.topic_id, e)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-red-400 p-1"
                        data-testid={`delete-topic-${t.topic_id}`}
                        aria-label="Delete topic"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <ChevronRight className="w-4 h-4 text-zinc-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default LearnMode;
